import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlaneTakeoff, Check, X } from "lucide-react";

const STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800" };

export default function LeavePage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const canDecide = ["master_admin", "super_admin", "principal", "hr_manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [children, setChildren] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ student_id: "", from_date: today, to_date: today, reason: "" });

  const load = () => api.get("/leave").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isParent) api.get("/parent/children").then((r) => { setChildren(r.data); setF((x) => ({ ...x, student_id: r.data[0]?.id || "" })); }).catch(() => {});
  }, [isParent]);

  async function save() {
    try { await api.post("/leave", { ...f, student_id: isParent ? f.student_id : null }); toast.success("Leave request sent"); setF({ ...f, reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function decide(id, status) {
    try { await api.patch(`/leave/${id}`, { status }); toast.success(`Leave ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Attendance" title="Leave" subtitle={canDecide ? "Review and approve leave requests." : "Request leave and track approval."} />
      {user?.role !== "master_admin" && (
        <div data-testid="leave-form" className="bg-white rounded-2xl shadow-sm p-5 mb-5 space-y-3">
          <h3 className="font-display font-bold text-slate-900">Request leave</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {isParent && (
              <div><Label>Child</Label>
                <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}>
                  <SelectTrigger data-testid="leave-child" className="mt-1"><SelectValue placeholder="Choose child" /></SelectTrigger>
                  <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div><Label>From</Label><Input data-testid="leave-from" type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} className="mt-1" /></div>
            <div><Label>To</Label><Input data-testid="leave-to" type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Reason</Label><Textarea data-testid="leave-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1" /></div>
          <Button data-testid="leave-save" onClick={save} disabled={!f.reason || (isParent && !f.student_id)} className="btn-brand">Send request</Button>
        </div>
      )}
      {rows.length === 0 ? <Empty testid="leave-empty" icon={PlaneTakeoff} text="No leave requests." /> : (
        <div className="space-y-3">
          {rows.map((l) => (
            <div key={l.id} data-testid={`leave-${l.id}`} className="bg-white rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-slate-900">{l.person_name}</span>
                  <Badge variant="outline" className="capitalize">{l.kind}</Badge>
                  <Badge className={`${STATUS[l.status]} hover:${STATUS[l.status]} capitalize`}>{l.status}</Badge>
                </div>
                <div className="text-sm text-slate-600 mt-1">{fmtDate(l.from_date)} → {fmtDate(l.to_date)} • {l.reason}</div>
                <div className="text-xs text-slate-400 mt-0.5">Requested by {l.requested_by_name}{l.decided_by ? ` • ${l.status} by ${l.decided_by}` : ""}</div>
              </div>
              {canDecide && l.status === "pending" && l.requested_by !== user.id && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-leave-${l.id}`} size="sm" onClick={() => decide(l.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" /> Approve</Button>
                  <Button data-testid={`reject-leave-${l.id}`} size="sm" variant="outline" onClick={() => decide(l.id, "rejected")} className="text-rose-600 border-rose-200"><X size={14} className="mr-1" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
